import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Users, Target, Wallet } from 'lucide-react';
import { DashboardStatsResponse } from '@clareefai/types';
import { useTranslation } from 'react-i18next';
import { useGetVendorBalanceQuery } from '../../store/vendorApi';

interface DashboardStatsProps {
  stats: DashboardStatsResponse | undefined;
  loading: boolean;
}

const DashboardStats: React.FC<DashboardStatsProps> = ({ stats, loading }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { data: balanceData, isLoading: balanceLoading } = useGetVendorBalanceQuery();

  if (loading || balanceLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {[...Array(4)].map((_, index) => (
          <div key={index} className="bg-white p-6 rounded-xl shadow-sm animate-pulse">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-8 bg-gray-200 rounded w-1/2 mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-1/4"></div>
              </div>
              <div className="w-12 h-12 bg-gray-200 rounded-lg"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!stats) return null;

  const formatChange = (change: number) => {
    const sign = change >= 0 ? '+' : '';
    const color = change >= 0 ? 'text-green-600' : 'text-red-600';
    return (
      <p className={`text-sm ${color}`}>
        {t('dashboard.stats.vsLastMonth', { sign, value: change.toFixed(1) })}
      </p>
    );
  };

  const displayStats = [
    {
      title: t('dashboard.totalMeetings'),
      value: stats.totalMeetings.toLocaleString(),
      icon: Calendar,
      color: 'bg-gradient-to-r from-blue-500 to-blue-600',
      change: stats.monthlyTrends?.meetings ? formatChange(stats.monthlyTrends.meetings.change) : null
    },
    {
      title: t('dashboard.activePromoters'),
      value: stats.activePromoters.toLocaleString(),
      icon: Users,
      color: 'bg-gradient-to-r from-green-500 to-emerald-600',
      change: stats.monthlyTrends?.activePromoters ? formatChange(stats.monthlyTrends.activePromoters.change) : null
    },
    {
      title: t('dashboard.prospects'),
      value: stats.totalProspects.toLocaleString(),
      icon: Target,
      color: 'bg-gradient-to-r from-purple-500 to-purple-600',
      change: stats.monthlyTrends?.prospects ? formatChange(stats.monthlyTrends.prospects.change) : null
    },
    {
      title: t('dashboard.stats.balance'),
      value: `${(balanceData?.balance || 0).toFixed(2)}€`,
      icon: Wallet,
      color: 'bg-gradient-to-r from-orange-500 to-red-500',
      change: null,
      clickable: true
    }
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      {displayStats.map((stat, index) => (
        <div 
          key={index} 
          className={`bg-white p-6 rounded-xl shadow-sm ${stat.clickable ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
          onClick={stat.clickable ? () => navigate('/rewards') : undefined}
        >
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-600">{stat.title}</p>
              <p className="text-2xl font-semibold text-gray-900">{stat.value}</p>
              {stat.change}
            </div>
            <div className={`p-3 rounded-lg ${stat.color}`}>
              <stat.icon className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default DashboardStats;